export type TParsedTask = {
	/** The ticket id as written ("CHR-15898", "chr-15898"), or null. */
	ticket: string | null;
	/** The task text without the ticket and its punctuation. */
	text: string;
	/**
	 * Everything stripped before/after `text`, verbatim ("[CHR-1] ",
	 * " (CHR-1)"): `${prefix}${text}${suffix}` gives the raw text back, so
	 * editing or reordering a task never rewrites how its ticket was typed.
	 */
	prefix: string;
	suffix: string;
};

// Separators between ticket and text: colon, dashes, pipe.
const SEP = "[:\\-–—|]";

const untouched = (raw: string): TParsedTask => ({
	ticket: null,
	text: raw,
	prefix: "",
	suffix: "",
});

const escape = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

// Built once per set of prefixes: a report renders the same project's
// tasks over and over.
const patternsCache = new Map<string, { leading: RegExp; trailing: RegExp }>();

const patternsFor = (prefixes: string[]) => {
	const cacheKey = prefixes.join("|");
	const cached = patternsCache.get(cacheKey);
	if (cached) return cached;

	// One of the project's keys, a dash, digits, and nothing word-like glued
	// after ("CHR-12abc" isn't one). Case-insensitive: tickets are often
	// typed lowercase.
	const key = `(?:${prefixes.map(escape).join("|")})-\\d+(?!\\w)`;
	const patterns = {
		// "CHR-1: text", "[CHR-1] text", "(chr-1) - text", "CHR-1 text"
		leading: new RegExp(
			`^(\\s*[\\[(]?(${key})[\\])]?\\s*${SEP}?\\s*)(.*)$`,
			"is" // the text can span several lines
		),
		// "text - CHR-1", "text (CHR-1)", "text [CHR-1]". A separator or
		// brackets are required here: a bare trailing key reads as part
		// of the sentence.
		trailing: new RegExp(
			`^(.*?)(\\s*(?:${SEP}\\s*[\\[(]?|[\\[(])(${key})[\\])]?\\s*)$`,
			"is"
		),
	};
	patternsCache.set(cacheKey, patterns);
	return patterns;
};

/**
 * Splits a ticket id off a task's text, at its start or its end — only a
 * ticket of one of the project's own `prefixes` ("CHR", "T4"). No
 * prefixes, no match, or nothing left once the ticket is removed: the raw
 * text, untouched. Guessing any key-shaped word ("UTF-8") would be worse
 * than no badge.
 */
export const parseTaskTicket = (
	raw: string,
	prefixes: string[] = []
): TParsedTask => {
	if (!prefixes.length) return untouched(raw);
	const { leading, trailing } = patternsFor(prefixes);

	const lead = raw.match(leading);
	if (lead && lead[3].trim())
		return { ticket: lead[2], text: lead[3], prefix: lead[1], suffix: "" };

	const trail = raw.match(trailing);
	if (trail && trail[1].trim())
		return {
			ticket: trail[3],
			text: trail[1],
			prefix: "",
			suffix: trail[2],
		};

	return untouched(raw);
};
